import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GiftDiscoveryPanel } from '../Components/admin/GiftDiscoveryPanel';

const API_URL = process.env.REACT_APP_API_URL || 'https://giftedict-backend.onrender.com';

// Stat Card Component
function StatCard({ title, value, change, changeType, icon, gradient, subtitle }) {
  return (
    <div className={`relative overflow-hidden rounded-2xl p-6 ${gradient} text-white shadow-xl`}>
      <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 rounded-full bg-white/10 blur-2xl"></div>
      <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-20 h-20 rounded-full bg-white/10 blur-xl"></div>

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <span className="text-3xl">{icon}</span>
          {change !== undefined && (
            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
              changeType === 'up' ? 'bg-green-400/20 text-green-100' :
              changeType === 'down' ? 'bg-red-400/20 text-red-100' :
              'bg-white/20'
            }`}>
              {changeType === 'up' ? '↑' : changeType === 'down' ? '↓' : ''} {change}
            </span>
          )}
        </div>
        <h3 className="text-4xl font-bold mb-1">{value}</h3>
        <p className="text-white/80 text-sm font-medium">{title}</p>
        {subtitle && <p className="text-white/60 text-xs mt-1">{subtitle}</p>}
      </div>
    </div>
  );
}

// User Row Component
function UserRow({ user, onViewDetails }) {
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
            user.isAdmin ? 'bg-gradient-to-br from-amber-400 to-orange-500' : 'bg-gradient-to-br from-indigo-400 to-purple-500'
          }`}>
            {user.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <p className="font-semibold text-slate-800">{user.name || 'No name'}</p>
            <p className="text-sm text-slate-500">{user.email}</p>
          </div>
          {user.isAdmin && (
            <span className="px-2 py-0.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold rounded-full">
              ADMIN
            </span>
          )}
        </div>
      </td>
      <td className="py-4 px-4">
        <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
          {user.giftCount} gifts
        </span>
      </td>
      <td className="py-4 px-4 text-slate-600">
        {new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
      </td>
      <td className="py-4 px-4">
        <button
          onClick={() => onViewDetails(user)}
          className="px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
        >
          View Details
        </button>
      </td>
    </tr>
  );
}

// Gift Row Component
function GiftRow({ gift, onUpdateStatus, onFindGift }) {
  const statusColors = {
    'new': 'bg-blue-100 text-blue-700',
    'pending': 'bg-amber-100 text-amber-700',
    'processing': 'bg-purple-100 text-purple-700',
    'completed': 'bg-green-100 text-green-700',
    'cancelled': 'bg-red-100 text-red-700',
  };

  // Calculate days until occasion
  const getDaysUntil = () => {
    if (!gift.date) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const occasionDate = new Date(gift.date);
    occasionDate.setFullYear(today.getFullYear());
    if (occasionDate < today) {
      occasionDate.setFullYear(today.getFullYear() + 1);
    }
    const diffTime = occasionDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const daysUntil = getDaysUntil();

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-coral-400 to-coral-600 flex items-center justify-center text-white font-bold">
            {gift.recipientName?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <p className="font-semibold text-slate-800">{gift.recipientName}</p>
            <p className="text-sm text-slate-500">{gift.occasion} • {gift.relationship}</p>
          </div>
        </div>
      </td>
      <td className="py-4 px-4">
        <div>
          <p className="text-slate-700">{gift.user?.name || 'Unknown'}</p>
          <p className="text-xs text-slate-500">{gift.user?.email}</p>
        </div>
      </td>
      <td className="py-4 px-4">
        <div>
          <span className="text-slate-600 font-medium">{gift.budget || '-'}</span>
          {gift.interests && (
            <p className="text-xs text-slate-400 truncate max-w-[150px]" title={gift.interests}>
              {gift.interests}
            </p>
          )}
        </div>
      </td>
      <td className="py-4 px-4">
        <div className="flex flex-col gap-1">
          {daysUntil !== null && (
            <span className={`px-2 py-0.5 rounded text-xs font-bold ${
              daysUntil <= 7 ? 'bg-red-100 text-red-700' :
              daysUntil <= 14 ? 'bg-amber-100 text-amber-700' :
              'bg-slate-100 text-slate-600'
            }`}>
              {daysUntil === 0 ? 'Today!' : daysUntil === 1 ? 'Tomorrow!' : `${daysUntil} days`}
            </span>
          )}
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[gift.status] || 'bg-slate-100 text-slate-700'}`}>
            {gift.status || 'new'}
          </span>
        </div>
      </td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onFindGift(gift)}
            className="px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 rounded-lg shadow-md shadow-indigo-500/20 transition-all hover:shadow-lg"
          >
            Find Gift
          </button>
          <select
            value={gift.status || 'new'}
            onChange={(e) => onUpdateStatus(gift.id, e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="new">New</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </td>
    </tr>
  );
}

// User Detail Modal
function UserDetailModal({ user, onClose }) {
  if (!user) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold">
                {user.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <h2 className="text-2xl font-bold">{user.name || 'No name'}</h2>
                <p className="text-white/80">{user.email}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-sm text-slate-500">Phone</p>
              <p className="font-medium text-slate-800">{user.phno || 'Not provided'}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-sm text-slate-500">Address</p>
              <p className="font-medium text-slate-800">{user.address || 'Not provided'}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-sm text-slate-500">Joined</p>
              <p className="font-medium text-slate-800">
                {new Date(user.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-sm text-slate-500">Total Gifts</p>
              <p className="font-medium text-slate-800">{user.giftCount || 0}</p>
            </div>
          </div>

          {/* User's Gifts */}
          <h3 className="font-bold text-lg text-slate-800 mb-4">Gift History</h3>
          {user.gifts && user.gifts.length > 0 ? (
            <div className="space-y-3">
              {user.gifts.map(gift => (
                <div key={gift.id} className="bg-slate-50 rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-800">{gift.recipientName}</p>
                    <p className="text-sm text-slate-500">{gift.occasion} • {gift.relationship}</p>
                  </div>
                  <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                    {gift.budget || 'No budget'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-4">No gifts yet</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Main Admin Dashboard
export function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedGiftForDiscovery, setSelectedGiftForDiscovery] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [adminInfo, setAdminInfo] = useState(null);
  const [giftSuggestions, setGiftSuggestions] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('authtoken');
    if (!token) {
      navigate('/signin');
      return;
    }

    checkAdminAccess(token);
  }, [navigate]);

  const checkAdminAccess = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/check`, {
        headers: { 'authtoken': token }
      });

      if (!response.ok) {
        navigate('/dashboard');
        return;
      }

      const data = await response.json();
      setAdminInfo(data.admin);
      fetchAllData(token);
    } catch (error) {
      console.error('Admin check failed:', error);
      navigate('/dashboard');
    }
  };

  const fetchAllData = async (token) => {
    setLoading(true);
    try {
      const [statsRes, usersRes, giftsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers: { 'authtoken': token } }),
        fetch(`${API_URL}/api/admin/users`, { headers: { 'authtoken': token } }),
        fetch(`${API_URL}/api/admin/gifts`, { headers: { 'authtoken': token } })
      ]);

      const [statsData, usersData, giftsData] = await Promise.all([
        statsRes.json(),
        usersRes.json(),
        giftsRes.json()
      ]);

      if (statsData.success) setStats(statsData.stats);
      if (usersData.success) setUsers(usersData.data.users);
      if (giftsData.success) {
        setGifts(giftsData.data.gifts);
        // Fetch suggestions for all gifts
        fetchSuggestionsForGifts(giftsData.data.gifts, token);
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestionsForGifts = async (gifts, token) => {
    try {
      const suggestionPromises = gifts.map(async (gift) => {
        try {
          const res = await fetch(`${API_URL}/api/suggestions/${gift.userId}/${gift.id}`, {
            headers: { 'authtoken': token }
          });
          const data = await res.json();
          if (data.success) {
            const accepted = (data.data || []).filter(s => s.status === 'accepted');
            return { giftId: gift.id, accepted };
          }
        } catch (err) {
          return { giftId: gift.id, accepted: [] };
        }
        return { giftId: gift.id, accepted: [] };
      });

      const results = await Promise.all(suggestionPromises);
      const suggestionsMap = {};
      results.forEach(({ giftId, accepted }) => {
        suggestionsMap[giftId] = accepted;
      });
      setGiftSuggestions(suggestionsMap);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const handleUpdateGiftStatus = async (giftId, newStatus) => {
    const token = localStorage.getItem('authtoken');
    try {
      const response = await fetch(`${API_URL}/api/admin/gifts/${giftId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'authtoken': token
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        setGifts(gifts.map(g => g.id === giftId ? { ...g, status: newStatus } : g));
      }
    } catch (error) {
      console.error('Error updating gift status:', error);
    }
  };

  const handleViewUserDetails = async (user) => {
    const token = localStorage.getItem('authtoken');
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${user.id}`, {
        headers: { 'authtoken': token }
      });
      const data = await response.json();
      if (data.success) {
        setSelectedUser(data.data);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredGifts = gifts.filter(gift =>
    (!statusFilter || gift.status === statusFilter) &&
    (gift.recipientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     gift.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
              >
                <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Admin Dashboard
                </h1>
                <p className="text-sm text-slate-500">
                  Welcome back, {adminInfo?.name || adminInfo?.email}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="px-3 py-1.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-sm font-bold rounded-full shadow-lg shadow-amber-500/30">
                ADMIN ACCESS
              </span>
              <button
                onClick={() => {
                  localStorage.removeItem('authtoken');
                  navigate('/signin');
                }}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex gap-8">
            {[
              { id: 'overview', label: 'Overview', icon: '📊' },
              { id: 'users', label: 'Users', icon: '👥' },
              { id: 'gifts', label: 'Gifts', icon: '🎁' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600 font-semibold'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-8">
            {/* ALL GIFT REQUESTS - Simplified Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-3 flex items-center justify-between">
                <h3 className="font-bold text-white">🎁 Gift Requests ({gifts.length})</h3>
              </div>

              <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left text-slate-500 text-xs uppercase">
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Recipient</th>
                      <th className="px-3 py-2">From</th>
                      <th className="px-3 py-2">Details</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Confirmed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(() => {
                      const sortedGifts = [...gifts].sort((a, b) => {
                        const dateA = a.date ? new Date(a.date) : new Date('9999-12-31');
                        const dateB = b.date ? new Date(b.date) : new Date('9999-12-31');
                        return dateA - dateB;
                      });

                      const today = new Date();
                      today.setHours(0, 0, 0, 0);

                      let currentMonth = '';

                      return sortedGifts.map(gift => {
                        const occasionDate = gift.date ? new Date(gift.date) : null;
                        const monthKey = occasionDate
                          ? `${occasionDate.toLocaleString('default', { month: 'short' })} ${occasionDate.getFullYear()}`
                          : 'No Date';
                        const showMonthHeader = monthKey !== currentMonth;
                        currentMonth = monthKey;

                        let daysUntil = null;
                        if (occasionDate) {
                          const diffTime = occasionDate - today;
                          daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        }

                        const statusColors = {
                          'new': 'bg-blue-100 text-blue-700',
                          'pending': 'bg-amber-100 text-amber-700',
                          'processing': 'bg-purple-100 text-purple-700',
                          'completed': 'bg-green-100 text-green-700',
                          'cancelled': 'bg-red-100 text-red-700',
                        };

                        return (
                          <React.Fragment key={gift.id}>
                            {showMonthHeader && (
                              <tr className="bg-slate-100">
                                <td colSpan="6" className="px-3 py-2 font-bold text-slate-600 text-xs">
                                  📅 {monthKey}
                                </td>
                              </tr>
                            )}
                            <tr
                              className="hover:bg-indigo-50 cursor-pointer transition-colors"
                              onClick={() => setSelectedGiftForDiscovery(gift)}
                            >
                              {/* Date & Days */}
                              <td className="px-3 py-2 whitespace-nowrap">
                                {occasionDate ? (
                                  <div>
                                    <div className="font-medium">{occasionDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                                      daysUntil <= 0 ? 'bg-red-500 text-white' :
                                      daysUntil <= 7 ? 'bg-red-100 text-red-700' :
                                      daysUntil <= 14 ? 'bg-amber-100 text-amber-700' :
                                      'bg-slate-100 text-slate-600'
                                    }`}>
                                      {daysUntil <= 0 ? 'NOW' : `${daysUntil}d`}
                                    </span>
                                  </div>
                                ) : <span className="text-slate-400">-</span>}
                              </td>
                              {/* Recipient */}
                              <td className="px-3 py-2">
                                <div className="font-semibold text-slate-800">{gift.recipientName}</div>
                                <div className="text-xs text-slate-500">{gift.occasion} • {gift.relationship}</div>
                              </td>
                              {/* User */}
                              <td className="px-3 py-2">
                                <div className="text-slate-700">{gift.user?.name || '-'}</div>
                                <div className="text-xs text-indigo-600">{gift.user?.email}</div>
                              </td>
                              {/* Details */}
                              <td className="px-3 py-2 max-w-[200px]">
                                <div className="text-emerald-600 font-medium">{gift.budget || '-'}</div>
                                {gift.interests && (
                                  <div className="text-xs text-slate-500 truncate" title={Array.isArray(gift.interests) ? gift.interests.join(', ') : gift.interests}>
                                    {Array.isArray(gift.interests) ? gift.interests.join(', ') : gift.interests}
                                  </div>
                                )}
                                {gift.interestsNote && (
                                  <div className="text-xs text-slate-400 italic truncate" title={gift.interestsNote}>
                                    "{gift.interestsNote}"
                                  </div>
                                )}
                              </td>
                              {/* Status */}
                              <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                <select
                                  value={gift.status || 'new'}
                                  onChange={(e) => handleUpdateGiftStatus(gift.id, e.target.value)}
                                  className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer ${statusColors[gift.status] || 'bg-slate-100 text-slate-700'}`}
                                >
                                  <option value="new">New</option>
                                  <option value="pending">Pending</option>
                                  <option value="processing">Processing</option>
                                  <option value="completed">Completed</option>
                                  <option value="cancelled">Cancelled</option>
                                </select>
                              </td>
                              {/* Confirmed */}
                              <td className="px-3 py-2">
                                {(() => {
                                  const acceptedSuggestions = giftSuggestions[gift.id] || [];
                                  if (acceptedSuggestions.length === 0) {
                                    return <span className="text-slate-400 text-xs">-</span>;
                                  }
                                  const firstAccepted = acceptedSuggestions[0];
                                  return (
                                    <div className="flex items-start gap-2">
                                      <div className="flex-shrink-0 w-8 h-8 rounded-lg overflow-hidden bg-slate-100">
                                        {firstAccepted.imageUrl && (
                                          <img
                                            src={firstAccepted.imageUrl}
                                            alt={firstAccepted.title}
                                            className="w-full h-full object-cover"
                                          />
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-xs font-semibold text-slate-800 truncate" title={firstAccepted.title}>
                                          {firstAccepted.title}
                                        </div>
                                        <div className="text-xs text-emerald-600 font-medium">
                                          {firstAccepted.price}
                                        </div>
                                        {acceptedSuggestions.length > 1 && (
                                          <div className="text-xs text-indigo-600 font-medium">
                                            +{acceptedSuggestions.length - 1} more
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      });
                    })()}
                  </tbody>
                </table>

                {gifts.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-slate-500">No gift requests yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Search */}
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">User</th>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Gifts</th>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Joined</th>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    <UserRow
                      key={user.id}
                      user={user}
                      onViewDetails={handleViewUserDetails}
                    />
                  ))}
                </tbody>
              </table>

              {filteredUsers.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-slate-500">No users found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Gifts Tab */}
        {activeTab === 'gifts' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search gifts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Status</option>
                <option value="new">New</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {/* Gifts Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Recipient</th>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">User</th>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Budget</th>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Status</th>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Update</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGifts.map(gift => (
                    <GiftRow
                      key={gift.id}
                      gift={gift}
                      onUpdateStatus={handleUpdateGiftStatus}
                      onFindGift={(gift) => setSelectedGiftForDiscovery(gift)}
                    />
                  ))}
                </tbody>
              </table>

              {filteredGifts.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-slate-500">No gifts found</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* User Detail Modal */}
      {selectedUser && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      )}

      {/* Gift Discovery Panel */}
      {selectedGiftForDiscovery && (
        <GiftDiscoveryPanel
          gift={selectedGiftForDiscovery}
          onClose={() => setSelectedGiftForDiscovery(null)}
          onSuggestionsUpdated={() => {
            // Refresh gifts list after suggestions are sent
            const token = localStorage.getItem('authtoken');
            fetchAllData(token);
          }}
        />
      )}
    </div>
  );
}

export default AdminDashboard;
